import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart, Filler, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend } from 'chart.js';
import Icon from '@mdi/react';
import { mdiPlus, mdiPencil, mdiDelete, mdiRefresh, mdiCash, mdiCloudDownload, mdiContentCopy, mdiChevronRight, mdiChevronDown } from '@mdi/js';
import { investmentsApi } from '../services/investments';
import { api } from '../services/api';
import { investmentAccounts } from '../utils/accounts';
import useIsMobile from '../hooks/useIsMobile';
import '../styles/InvestmentsPage.css';

Chart.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler
);

const SECURITY_TYPES = ['STOCK', 'ETF'];
const DEPOSIT_TYPES = ['FIXED_DEPOSIT', 'VARIABLE_DEPOSIT'];
const ITX_TYPES = ['BUY', 'SELL', 'DIVIDEND', 'FEES', 'TAXES', 'DEPOSIT', 'WITHDRAWAL', 'INTEREST'];
const INVESTMENT_TYPES = ['STOCK', 'ETF', 'FIXED_DEPOSIT', 'VARIABLE_DEPOSIT', 'MORTGAGE'];
const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY'];

const currencySymbol = (code) => {
  switch (code) {
    case 'EUR': return '€';
    case 'USD': return '$';
    case 'GBP': return '£';
    case 'CHF': return 'Fr';
    case 'JPY': return '¥';
    default: return '';
  }
};

const fmt = (v, digits = 2) => (v === null || v === undefined ? '—' : Number(v).toFixed(digits));
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`);
const signClass = (v) => (v > 0 ? 'positive' : v < 0 ? 'negative' : '');

// Cash-flow direction of an investment transaction. itx amounts are stored
// positive for every type; the cash direction comes from CASH_FLOW_SIGN.
const ITX_CASH_FLOW_SIGN = {
  BUY: -1,
  SELL: 1,
  DIVIDEND: 1,
  FEES: -1,
  TAXES: -1,
  DEPOSIT: 1,
  WITHDRAWAL: -1,
  INTEREST: 1,
};

// FEES/TAXES cash out (red), DIVIDEND/INTEREST cash in (green),
// BUY/SELL/others follow the stored sign.
const itxSign = (t) => {
  const sign = ITX_CASH_FLOW_SIGN[t.type];
  if (sign !== undefined) return sign;
  return t.amount > 0 ? 1 : t.amount < 0 ? -1 : 0;
};

const itxDisplay = (t) => {
  const sign = ITX_CASH_FLOW_SIGN[t.type];
  if (sign === -1) return `−${fmt(t.amount)}`;
  if (sign === 1) return `+${fmt(t.amount)}`;
  return fmt(t.amount);
};

const itxSignClass = (t) => {
  const sign = ITX_CASH_FLOW_SIGN[t.type];
  if (sign === -1) return 'negative';
  if (sign === 1) return 'positive';
  return signClass(t.amount);
};

const todayString = () => new Date().toISOString().substring(0, 10);

const InvestmentsPage = () => {
  const [portfolio, setPortfolio] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAccountId, setActiveAccountId] = useState('all');
  const [downloadingPrices, setDownloadingPrices] = useState(false);
  // Result of the global price download: {updated: [...], failed: [...]} | null
  const [priceRefresh, setPriceRefresh] = useState(null);

  // Panel state: {mode: 'new'|'edit'|'tx'|'price'|'apy', investment, savings, account}
  // For 'apy' on savings accounts, `savings` carries the entry and `investment` is null.
  const [panel, setPanel] = useState(null);
  const [detail, setDetail] = useState(null); // expanded investment row (tx list)
  const [investmentTx, setInvestmentTx] = useState([]);
  const [valueHistory, setValueHistory] = useState(null);
  // Per-ticker daily close prices for the price-history chart
  const [priceHistory, setPriceHistory] = useState(null);
  const [priceHistoryState, setPriceHistoryState] = useState('idle'); // idle|loading|ready|error
  // Chart focus: 'all' shows every security, otherwise `${ticker}|${currency}`
  const [focusedTicker, setFocusedTicker] = useState('all');
  // Collapsed-by-default sections (progressive disclosure)
  const [expandedAccounts, setExpandedAccounts] = useState(() => new Set());
  const [showByTicker, setShowByTicker] = useState(false);
  const isMobile = useIsMobile();

  const toggleAccount = (accountId) => {
    const key = String(accountId);
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const fetchPortfolio = async () => {
    try {
      const [pf, accs, vh] = await Promise.all([
        investmentsApi.portfolio(),
        api.get('/accounts'),
        investmentsApi.valueHistory(),
      ]);
      setPortfolio(pf);
      setAccounts(investmentAccounts(accs.data));
      setValueHistory(vh);
    } catch (e) {
      console.error('Error fetching portfolio:', e);
      setError('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolio();
  }, []);

  // Price history is fetched separately: it can be slow (per-ticker upstream
  // calls) and shouldn't block or fail the rest of the page.
  useEffect(() => {
    let cancelled = false;
    setPriceHistoryState('loading');
    investmentsApi.priceHistory(30)
      .then((data) => {
        if (cancelled) return;
        setPriceHistory(data);
        setPriceHistoryState('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('Error fetching price history:', e);
        setPriceHistoryState('error');
      });
    return () => { cancelled = true; };
  }, []);

  const visibleAccounts = useMemo(() => {
    if (!portfolio) return [];
    return portfolio.accounts.filter(
      (a) => activeAccountId === 'all' || String(a.account_id) === String(activeAccountId)
    );
  }, [portfolio, activeAccountId]);

  const openDetail = async (investment) => {
    if (detail && detail.id === investment.id) {
      setDetail(null);
      return;
    }
    setDetail(investment);
    try {
      const rows = await investmentsApi.transactions(investment.id);
      setInvestmentTx(rows);
    } catch (e) {
      console.error('Error fetching investment transactions:', e);
      setInvestmentTx([]);
    }
  };

  // Savings entries carry their (embedded) APY history — no transaction fetch needed.
  const openSavingsDetail = (savings) => {
    setDetail(detail && detail.id === savings.id ? null : savings);
  };

  const refreshAll = () => {
    setLoading(true);
    fetchPortfolio();
    if (detail && detail.kind !== 'savings') {
      investmentsApi.transactions(detail.id).then(setInvestmentTx).catch(() => setInvestmentTx([]));
    }
  };

  const downloadPrices = async () => {
    setDownloadingPrices(true);
    setError(null);
    setPriceRefresh(null);
    try {
      const result = await investmentsApi.refreshPrices();
      setPriceRefresh(result);
      if (result.updated_count > 0) {
        fetchPortfolio();
      }
    } catch (e) {
      console.error('Error downloading prices:', e);
      setError('Failed to download prices');
    } finally {
      setDownloadingPrices(false);
    }
  };

  const totals = portfolio?.totals || { invested: 0, current_value: 0, pl_value: 0, pl_percent: null };
  const baseCurrency = portfolio?.totals?.currency || portfolio?.base_currency || '';
  const byCurrency = portfolio?.by_currency || [];

  // Lifetime fees & income, split by currency (never mix EUR/USD sums).
  // The '+' makes explicit these are addends to be summed, not a conversion.
  const feesByCurrency = portfolio?.fees_by_currency || {};
  const incomeByCurrency = portfolio?.income_by_currency || {};
  const currencyBreakdown = (buckets) =>
    Object.entries(buckets)
      .filter(([, v]) => Math.abs(v) > 0.005)
      .map(([cur, v]) => `${currencySymbol(cur)}${fmt(Math.abs(v))}`)
      .join(' + ');

  const summary = [
    { label: baseCurrency ? `Invested (${baseCurrency})` : 'Invested', value: fmt(totals.invested), cls: '' },
    { label: baseCurrency ? `Current value (${baseCurrency})` : 'Current value', value: fmt(totals.current_value), cls: '' },
    {
      label: 'Total P/L',
      value: `${totals.pl_value >= 0 ? '+' : ''}${fmt(totals.pl_value)}`,
      cls: signClass(totals.pl_value),
    },
    { label: 'Total P/L %', value: fmtPct(totals.pl_percent), cls: signClass(totals.pl_percent) },
    { label: 'Fees paid', value: currencyBreakdown(feesByCurrency) || '—', cls: 'negative' },
    { label: 'Dividends received', value: currencyBreakdown(incomeByCurrency) || '—', cls: 'positive' },
  ];

  // Cash per currency (EUR and USD tracked separately by the platform; summing
  // them would be meaningless). Each account's own cash is always in its
  // currency, so buckets never mix.
  const cashByCurrency = byCurrency.filter((b) => Math.abs(b.cash) > 0.005);

  // Value history: per account, cumulative invested capital from the ledger
  // plus today's actual valuation as the final point.
  // Value history: per account, account value over time (contributed capital
  // from the ledger) with today's actual valuation as the final point.
  const historyData = useMemo(() => {
    if (!valueHistory || !valueHistory.accounts?.length) return null;
    // Union of months across accounts (accounts may start at different dates)
    const monthSet = new Set();
    valueHistory.accounts.forEach((acc) => {
      (acc.value_series || []).forEach((p) => monthSet.add(p.month));
    });
    const axis = [...monthSet].sort();
    axis.push(valueHistory.as_of);

    const datasets = [];
    const palette = [
      'rgba(75, 192, 192, 1)', 'rgba(255, 159, 64, 1)', 'rgba(153, 102, 255, 1)',
    ];
    valueHistory.accounts.forEach((acc, i) => {
      const byMonth = {};
      (acc.value_series || []).forEach((p) => { byMonth[p.month] = p.value; });
      const color = palette[i % palette.length];
      datasets.push({
        label: acc.account_name,
        data: axis.map((m) => (
          m === valueHistory.as_of
            ? (acc.current_value ?? null)
            : (byMonth[m] ?? null)
        )),
        borderColor: color,
        backgroundColor: color.replace('1)', '0.15)'),
        fill: false,
        tension: 0.15,
        spanGaps: true,
        pointRadius: 3,
        pointHitRadius: 8,
      });
    });
    return { labels: axis, datasets };
  }, [valueHistory, portfolio]);

  const chartOptions = () => {
    const fontSize = isMobile ? 10 : 14;
    const textColor = getComputedStyle(document.body).getPropertyValue('--color-text');
    return {
      responsive: true,
      maintainAspectRatio: false,
      spanGaps: true,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { labels: { color: textColor, font: { size: fontSize } } },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items) => {
              const raw = items[0]?.label ?? '';
              if (isMobile || !/^(19|20)\d{2}-\d{2}$/.test(raw)) return raw;
              const d = new Date(raw + '-01T00:00:00');
              return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
            },
            label: (item) => `${item.dataset.label}: ${Number(item.parsed.y).toFixed(2)}`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColor,
            font: { size: fontSize },
            maxRotation: 45,
            minRotation: 35,
            autoSkip: true,
            maxTicksLimit: isMobile ? 8 : 14,
            callback(value) {
              const label = this.getLabelForValue(value);
              if (!label) return label;
              if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
                return new Date(label).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              }
              const [y, mo] = label.split('-');
              const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return `${names[parseInt(mo, 10) - 1] || mo} ${String(y).slice(2)}`;
            },
          },
        },
        y: { ticks: { color: textColor, font: { size: fontSize } } },
      },
    };
  };

  // Series that actually have data (backend includes failed tickers with
  // empty points + an error; those don't get a line).
  const priceSeries = useMemo(() => {
    if (!priceHistory || !Array.isArray(priceHistory.series)) return [];
    return priceHistory.series.filter(
      (s) => s && Array.isArray(s.points) && s.points.length > 0,
    );
  }, [priceHistory]);

  const priceChartData = useMemo(() => {
    if (!priceSeries.length) return null;
    // Union of trading dates across securities (markets close on different days)
    const dateSet = new Set();
    priceSeries.forEach((s) => s.points.forEach((p) => dateSet.add(p.date)));
    const axis = [...dateSet].sort();

    const palette = [
      'rgba(75, 192, 192, 1)', 'rgba(255, 159, 64, 1)', 'rgba(153, 102, 255, 1)',
      'rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)', 'rgba(255, 206, 86, 1)',
      'rgba(120, 200, 90, 1)', 'rgba(186, 85, 211, 1)', 'rgba(40, 113, 183, 1)',
      'rgba(230, 126, 34, 1)',
    ];
    const focused = focusedTicker !== 'all';
    // Alphabetical order keeps the tooltip deterministic (itemSort in
    // priceChartOptions is the runtime guarantee; this covers the legend).
    const visible = (focused
      ? priceSeries.filter((s) => `${s.ticker}|${s.currency}` === focusedTicker)
      : priceSeries.slice()
    ).sort((a, b) => `${a.ticker} (${a.currency})`.localeCompare(`${b.ticker} (${b.currency})`));
    const datasets = visible.map((s, i) => {
      const color = focused ? palette[0] : palette[i % palette.length];
      const byDate = {};
      s.points.forEach((p) => { byDate[p.date] = p.price; });
      return {
        label: focused ? s.ticker : `${s.ticker} (${s.currency})`,
        data: axis.map((d) => byDate[d] ?? null),
        currency: s.currency,
        borderColor: color,
        backgroundColor: color.replace('1)', '0.15)'),
        fill: false,
        tension: 0.15,
        spanGaps: true,
        pointRadius: 2,
        pointHitRadius: 8,
      };
    });
    return { labels: axis, datasets };
  }, [priceSeries, focusedTicker]);

  const priceChartOptions = () => {
    const fontSize = isMobile ? 10 : 14;
    const textColor = getComputedStyle(document.body).getPropertyValue('--color-text');
    return {
      responsive: true,
      maintainAspectRatio: false,
      spanGaps: true,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          // Chart.js 4 sorts tooltip items with `itemSort` (a plain tooltip
          // `sort` key is not a supported option and is silently ignored).
          itemSort: (a, b) => String(a.dataset?.label ?? '')
            .localeCompare(String(b.dataset?.label ?? '')),
          callbacks: {
            title: (items) => {
              const raw = items[0]?.label ?? '';
              if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                return new Date(`${raw}T00:00:00`).toLocaleDateString(undefined, {
                  year: 'numeric', month: 'short', day: 'numeric',
                });
              }
              return raw;
            },
            label: (item) => (
              `${item.dataset.label}: ${currencySymbol(item.dataset.currency || '')}`
              + `${Number(item.parsed.y).toFixed(4)}`
            ),
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColor,
            font: { size: fontSize },
            maxRotation: 45,
            minRotation: 35,
            autoSkip: true,
            maxTicksLimit: isMobile ? 8 : 14,
            callback(value) {
              const label = this.getLabelForValue(value);
              if (!label) return label;
              return new Date(`${label}T00:00:00`).toLocaleDateString(undefined, {
                month: 'short', day: 'numeric',
              });
            },
          },
        },
        y: { ticks: { color: textColor, font: { size: fontSize } } },
      },
    };
  };

  const isSecurity = (h) => SECURITY_TYPES.includes(h.type);
  const isDeposit = (h) => DEPOSIT_TYPES.includes(h.type);

  // Group a list of holdings by their native currency (account currency
  // first), preserving order within each group.
  const groupByCurrency = (holdings) => {
    const groups = new Map();
    for (const h of holdings) {
      const cur = (h.currency || '—').toUpperCase();
      if (!groups.has(cur)) groups.set(cur, []);
      groups.get(cur).push(h);
    }
    // Prefer the account's own currency first, then alphabetical.
    return [...groups.entries()].sort(([a], [b]) => {
      if (a === b) return 0;
      if (a === '—') return 1;
      if (b === '—') return -1;
      return a.localeCompare(b);
    });
  };

  return (
    <div className="card dashboard-container investments-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      <div className="dashboard-header">
        <h2 className="page-title">Investments</h2>
        <div className="header">
          <div className="buttons-bar">
            <button
              className="btn btn--ghost"
              onClick={downloadPrices}
              disabled={downloadingPrices || accounts.length === 0}
              title="Download current prices from the internet"
            >
              <Icon path={mdiCloudDownload} size={0.8} />
            </button>
            <button className="btn btn--ghost" onClick={refreshAll} title="Refresh">
              <Icon path={mdiRefresh} size={0.8} />
            </button>
            <button
              className="btn btn--primary"
              onClick={() => setPanel({ mode: 'new', account: null })}
              disabled={accounts.length === 0}
            >
              <Icon path={mdiPlus} size={0.8} />
              New Investment
            </button>
          </div>
        </div>
      </div>

      {error && <div className="investments-error">{error}</div>}

      {priceRefresh && (
        <div
          className={`price-refresh-panel ${priceRefresh.failed_count > 0 ? 'price-refresh-panel--partial' : 'price-refresh-panel--ok'}`}
          role="status"
        >
          {priceRefresh.failed_count === 0 ? (
            <span>
              Downloaded current prices for {priceRefresh.updated_count}{' '}
              {priceRefresh.updated_count === 1 ? 'ticker' : 'tickers'}.
            </span>
          ) : (
            <span>
              Downloaded {priceRefresh.updated_count}{' '}
              {priceRefresh.updated_count === 1 ? 'price' : 'prices'} — could not fetch{' '}
              {priceRefresh.failed.map((f) => f.ticker).join(', ')} (no online quote found).
            </span>
          )}
          <button
            className="price-refresh-panel__close"
            aria-label="Dismiss"
            onClick={() => setPriceRefresh(null)}
          >
            ×
          </button>
        </div>
      )}

      {accounts.length === 0 && !loading && (
        <div className="investments-empty">
          <p>No investment accounts yet.</p>
          <p>
            Create an account with type <strong>Investment</strong> in{' '}
            <a href="/manage-accounts">Manage Accounts</a> (e.g. "eToro", "Trading212"), then add
            your products here.
          </p>
        </div>
      )}

      {portfolio && accounts.length > 0 && (
        <>
          <div className="portfolio-summary">
            {summary.map((s) => (
              <div className="summary-card" key={s.label}>
                <span className="summary-card__label">{s.label}</span>
                <span className={`summary-card__value ${s.cls}`}>{s.value}</span>
              </div>
            ))}
            <div className="summary-card">
              <span className="summary-card__label">Cash available</span>
              <span className="summary-card__value">
                {cashByCurrency.length === 0
                  ? '0.00'
                  : cashByCurrency.map((b, i) => (
                      <span className="cash-currency" key={b.currency}>
                        {i > 0 && ' + '}
                        {currencySymbol(b.currency)} {fmt(b.cash)}
                      </span>
                    ))}
              </span>
            </div>
          </div>

          {visibleAccounts.map((acc) => {
            const hasSecurities = acc.holdings.some(isSecurity);
            const hasDeposits = acc.holdings.some(isDeposit);
            const isExpanded = expandedAccounts.has(String(acc.account_id));
            return (
              <div
                className={`investment-account-section ${isExpanded ? 'expanded' : ''}`}
                key={acc.account_id}
              >
                <button
                  type="button"
                  className="account-section-header"
                  onClick={() => toggleAccount(acc.account_id)}
                  aria-expanded={isExpanded}
                >
                  <span className="account-section-title">
                    <Icon path={isExpanded ? mdiChevronDown : mdiChevronRight} size={0.9} />
                    <span className="account-section-name">
                      {acc.account_name} <span className="account-currency">({acc.currency})</span>
                    </span>
                  </span>
                  <div className="account-section-totals">
                    <span>
                      Invested: <strong>{fmt(acc.invested)}</strong>
                    </span>
                    <span>
                      Value: <strong>{fmt((acc.holdings_value || 0) + (acc.savings_value || 0))}</strong>
                    </span>
                    <span className={signClass(acc.pl_value)}>
                      P/L: <strong>{acc.pl_value >= 0 ? '+' : ''}{fmt(acc.pl_value)}</strong> (
                      {fmtPct(acc.pl_percent)})
                    </span>
                    {(acc.savings || []).length === 0 && (
                    <span>
                      Cash: <strong>
                        {(Object.entries(acc.cash_by_currency || {})
                          .filter(([, v]) => Math.abs(v) > 0.005)
                          .map(([cur, amount]) => `${currencySymbol(cur)} ${fmt(amount)}`)
                          .join(' + ')) || `${currencySymbol(acc.currency)} ${fmt(acc.cash_balance)}`}
                    </strong>
                    </span>
                    )}
                    <span>
                      Total: <strong>{fmt(acc.total_value)}</strong>
                    </span>
                  </div>
                </button>

                {isExpanded && (
                  <>
                    {hasSecurities && (
                      <div className="table-scroll">
                        <table className="holdings-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Price</th>
                            <th>Value</th>
                            <th>P/L</th>
                            <th>P/L %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupByCurrency(acc.holdings.filter(isSecurity)).map(([cur, rows]) => (
                            <React.Fragment key={cur}>
                              <tr className="currency-group-row">
                                <td colSpan={5}>{cur}</td>
                              </tr>
                              {rows.map((h) => (
                                <SecurityRow
                                  key={h.id}
                                  h={h}
                                  detail={detail}
                                  openDetail={openDetail}
                                  setPanel={setPanel}
                                  investmentTx={investmentTx}
                                  refreshAll={refreshAll}
                                />
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                        </table>
                      </div>
                    )}

                    {hasDeposits && (
                      <div className="table-scroll">
                        <table className="holdings-table deposits-table">
                        <thead>
                          <tr>
                            <th>Deposit</th>
                            <th>Estimated value</th>
                            <th>Est. return</th>
                            {!isMobile && <th>APY</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {acc.holdings.filter(isDeposit).map((h) => (
                            <React.Fragment key={h.id}>
                              <tr
                                className={`holding-row ${detail && detail.id === h.id ? 'expanded' : ''}`}
                                onClick={() => openDetail(h)}
                              >
                                <td>{h.name}</td>
                                <td>{fmt(h.estimated_value)}</td>
                                <td className={signClass(h.estimated_return)}>
                                  +{fmt(h.estimated_return)}
                                </td>
                                {!isMobile && <td>{fmt(h.current_apy)}%</td>}
                              </tr>
                              {detail && detail.id === h.id && (
                                <tr className="detail-row">
                                  <td colSpan={isMobile ? 3 : 4}>
                                    <div className="detail-inner">
                                      <div className="detail-stats">
                                        <div className="detail-stat">
                                          <span className="detail-stat__label">Balance</span>
                                          <span className="detail-stat__value">{fmt(h.balance)}</span>
                                        </div>
                                        <div className="detail-stat">
                                          <span className="detail-stat__label">APY</span>
                                          <span className="detail-stat__value">{fmt(h.current_apy)}%</span>
                                        </div>
                                        <div className="detail-stat">
                                          <span className="detail-stat__label">Estimated return</span>
                                          <span className="detail-stat__value positive">+{fmt(h.estimated_return)}</span>
                                        </div>
                                        <div className="detail-stat">
                                          <span className="detail-stat__label">Estimated value</span>
                                          <span className="detail-stat__value">{fmt(h.estimated_value)}</span>
                                        </div>
                                      </div>
                                      <h4>Transactions — {h.name}</h4>
                                      {investmentTx.length === 0 ? (
                                        <p className="no-transactions">No transactions recorded</p>
                                      ) : (
                                        <table className="detail-table">
                                          <thead>
                                            <tr>
                                              <th>Date</th>
                                              <th>Type</th>
                                              <th>Amount</th>
                                              <th></th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {investmentTx.map((t) => (
                                              <tr key={t.id}>
                                                <td>{t.transaction_date}</td>
                                                <td>{t.type}</td>
                                                <td className={signClass(t.amount)}>{fmt(t.amount)}</td>
                                                <td>
                                                  <button
                                                    className="btn btn--ghost"
                                                    onClick={() => {
                                                      if (window.confirm('Delete this transaction? The linked main transaction will also be removed.')) {
                                                        investmentsApi
                                                          .removeTransaction(h.id, t.id)
                                                          .then(refreshAll);
                                                      }
                                                    }}
                                                  >
                                                    <Icon path={mdiDelete} size={0.7} />
                                                  </button>
                                                </td>
                                              </tr>
                                             ))}
                                           </tbody>
                                         </table>
                                       )}
                                       <div className="detail-actions">
                                        <button
                                          className="btn btn--ghost"
                                          title="Update APY"
                                          onClick={() => setPanel({ mode: 'apy', investment: h })}
                                        >
                                          <Icon path={mdiPencil} size={0.7} /> Update APY
                                        </button>
                                        <button
                                          className="btn btn--ghost"
                                          onClick={() => setPanel({ mode: 'tx', investment: h, presetType: 'INTEREST' })}
                                        >
                                          <Icon path={mdiPlus} size={0.7} /> Record actual return
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                        </table>
                      </div>
                    )}

                    {(acc.savings || []).length > 0 && (
                      <div className="table-scroll">
                        <table className="holdings-table deposits-table savings-table">
                        <thead>
                          <tr>
                            <th>Savings</th>
                            <th>Estimated value</th>
                            <th>Est. return</th>
                            {!isMobile && <th>APY</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {acc.savings.map((s) => (
                            <React.Fragment key={s.id}>
                              <tr
                                className={`holding-row ${detail && detail.id === s.id ? 'expanded' : ''}`}
                                onClick={() => openSavingsDetail(s)}
                              >
                                <td>{s.name}</td>
                                <td>{fmt(s.estimated_value)}</td>
                                <td className={signClass(s.estimated_return)}>
                                  {(s.estimated_return ?? 0) >= 0 ? '+' : ''}{fmt(s.estimated_return)}
                                </td>
                                {!isMobile && (
                                  <td>{s.current_apy != null ? `${fmt(s.current_apy)}%` : '—'}</td>
                                )}
                              </tr>
                              {detail && detail.id === s.id && (
                                <tr className="detail-row">
                                  <td colSpan={isMobile ? 3 : 4}>
                                    <div className="detail-inner">
                                      <div className="detail-stats">
                                        <div className="detail-stat">
                                          <span className="detail-stat__label">Balance</span>
                                          <span className="detail-stat__value">{fmt(s.balance)}</span>
                                        </div>
                                        <div className="detail-stat">
                                          <span className="detail-stat__label">APY</span>
                                          <span className="detail-stat__value">
                                            {s.current_apy != null ? `${fmt(s.current_apy)}%` : '—'}
                                          </span>
                                        </div>
                                        <div className="detail-stat">
                                          <span className="detail-stat__label">Estimated return</span>
                                          <span className={`detail-stat__value ${signClass(s.estimated_return)}`}>
                                            {(s.estimated_return ?? 0) >= 0 ? '+' : ''}{fmt(s.estimated_return)}
                                          </span>
                                        </div>
                                        <div className="detail-stat">
                                          <span className="detail-stat__label">Estimated value</span>
                                          <span className="detail-stat__value">{fmt(s.estimated_value)}</span>
                                        </div>
                                        {s.last_transaction_date && (
                                          <div className="detail-stat">
                                            <span className="detail-stat__label">Last transaction</span>
                                            <span className="detail-stat__value">{s.last_transaction_date}</span>
                                          </div>
                                        )}
                                      </div>
                                      <h4>APY history — {s.name}</h4>
                                      {(s.apy_history || []).length === 0 ? (
                                        <p className="no-transactions">No APY recorded yet</p>
                                      ) : (
                                        <table className="detail-table apy-history-table">
                                          <thead>
                                            <tr>
                                              <th>Effective from</th>
                                              <th>APY</th>
                                              <th></th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(s.apy_history || []).map((e) => (
                                              <tr key={e.id}>
                                                <td>{e.effective_date}</td>
                                                <td>{fmt(e.apy)}%</td>
                                                <td>
                                                  <button
                                                    className="btn btn--ghost"
                                                    title="Delete APY entry"
                                                    onClick={() => {
                                                      if (window.confirm('Delete this APY entry? Estimates will recompute from the remaining entries.')) {
                                                        investmentsApi
                                                          .removeSavingsApy(e.id)
                                                          .then(refreshAll);
                                                      }
                                                    }}
                                                  >
                                                    <Icon path={mdiDelete} size={0.7} />
                                                  </button>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                      <div className="detail-actions">
                                        <button
                                          className="btn btn--ghost"
                                          title={s.current_apy == null ? 'Add APY' : 'Update APY'}
                                          onClick={() => setPanel({ mode: 'apy', savings: s })}
                                        >
                                          <Icon path={mdiPencil} size={0.7} />{' '}
                                          {s.current_apy == null ? 'Add APY' : 'Update APY'}
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                        </table>
                      </div>
                    )}
                  </>
                 )}
               </div>
            );
          })}

          {portfolio.by_ticker.length > 0 && (
            <div className="by-ticker-section">
              <button
                type="button"
                className="account-section-header account-section-header--plain"
                onClick={() => setShowByTicker((v) => !v)}
                aria-expanded={showByTicker}
              >
                <span className="account-section-title">
                  <Icon path={showByTicker ? mdiChevronDown : mdiChevronRight} size={0.9} />
                  <span className="account-section-name">Performance by product (across accounts)</span>
                </span>
              </button>
              {showByTicker && (
                <div className="table-scroll">
                  <table className="holdings-table">
                  <thead>
                    <tr>
                      <th>Ticker</th>
                      <th>Currency</th>
                      <th>Total qty</th>
                      <th>Invested</th>
                      <th>Value</th>
                      <th>P/L</th>
                      <th>P/L %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupByCurrency(portfolio.by_ticker).map(([cur, rows]) => (
                      <React.Fragment key={cur}>
                        <tr className="currency-group-row">
                          <td colSpan={7}>{cur}</td>
                        </tr>
                        {rows.map((t) => (
                          <tr key={`${t.ticker}-${t.currency}`}>
                            <td>{t.ticker}</td>
                            <td>{t.currency}</td>
                            <td>{fmt(t.quantity, 4)}</td>
                            <td>{fmt(t.invested)}</td>
                            <td>{fmt(t.current_value)}</td>
                            <td className={signClass(t.pl_value)}>
                              {t.pl_value >= 0 ? '+' : ''}
                              {fmt(t.pl_value)}
                            </td>
                          <td className={signClass(t.pl_percent)}>{fmtPct(t.pl_percent)}</td>
                        </tr>
                      ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {historyData && (
            <div className="chart-wrapper investments-chart">
              <h2>Account value over time</h2>
              <p className="chart-note">
                Historical points show the net money you contributed (deposits, dividends, minus
                spending) per account; the final point is the account's actual value today.
              </p>
              <Line data={historyData} options={chartOptions()} />
            </div>
          )}

          {priceHistoryState !== 'idle' && (
            <div className="chart-wrapper investments-chart">
              <h2>Price history — last month</h2>
              <p className="chart-note">
                Daily market close for each stock/ETF you hold, in the holding's currency.
                Deposits don't have market prices and are not shown. Sources may be delayed.
              </p>
              {priceHistoryState === 'loading' && (
                <p className="chart-note">Loading recent prices…</p>
              )}
              {priceHistoryState === 'error' && (
                <p className="chart-note">Couldn't load price history right now.</p>
              )}
              {priceHistoryState === 'ready' && priceSeries.length === 0 && (
                <p className="chart-note">
                  No price history available yet — try “Download prices” first.
                </p>
              )}
              {priceHistoryState === 'ready' && priceSeries.length > 0 && (
                <>
                  <div className="chart-controls">
                    <label htmlFor="price-history-focus">Focus on</label>
                    <select
                      id="price-history-focus"
                      value={focusedTicker}
                      onChange={(e) => setFocusedTicker(e.target.value)}
                    >
                      <option value="all">All investments</option>
                      {priceSeries.map((s) => (
                        <option key={`${s.ticker}|${s.currency}`} value={`${s.ticker}|${s.currency}`}>
                          {s.ticker} ({s.currency})
                        </option>
                      ))}
                    </select>
                  </div>
                  {priceChartData && <Line data={priceChartData} options={priceChartOptions()} />}
                </>
              )}
            </div>
          )}
        </>
      )}

      {panel && (
        <div className="edit-panel create-panel">
          <div className="panel-header">
            <h3>
              {panel.mode === 'new' && 'New Investment'}
              {panel.mode === 'edit' && 'Edit Investment'}
              {panel.mode === 'tx' && `Transaction — ${panel.investment.name}`}
              {panel.mode === 'tx-edit' && `Edit transaction — ${panel.investment.name}`}
              {panel.mode === 'tx-dup' && `Duplicate transaction — ${panel.investment.name}`}
              {panel.mode === 'price' && `Update price — ${panel.investment.ticker}`}
              {panel.mode === 'apy' && (panel.savings
                ? `${panel.savings.current_apy == null ? 'Add APY' : 'Update APY'} — ${panel.savings.name}`
                : `Update APY — ${panel.investment.name}`)}
            </h3>
            <button onClick={() => setPanel(null)}>×</button>
          </div>
          <InvestmentForm
            mode={panel.mode}
            investment={panel.investment}
            savings={panel.savings}
            account={panel.account}
            presetType={panel.presetType}
            tx={panel.tx}
            accounts={accounts}
            onCancel={() => setPanel(null)}
            onSuccess={() => {
              setPanel(null);
              refreshAll();
            }}
          />
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------
// Security holding row (+ expandable transaction detail)
// ------------------------------------------------------------------

const SecurityRow = ({ h, detail, openDetail, setPanel, investmentTx, refreshAll }) => {
  const label = h.ticker || h.name;
  const isExpanded = detail && detail.id === h.id;
  return (
    <React.Fragment>
      <tr
        className={`holding-row ${isExpanded ? 'expanded' : ''}`}
        onClick={() => openDetail(h)}
      >
        <td className="holding-name">
          <span className="holding-chevron">
            <Icon path={isExpanded ? mdiChevronDown : mdiChevronRight} size={0.7} />
          </span>
          <span className="holding-name-text">
            {label}
            {h.ticker && h.name !== h.ticker && (
              <span className="holding-name-sub">{h.name}</span>
            )}
          </span>
        </td>
        <td>{fmt(h.current_price)}</td>
        <td>{fmt(h.current_value)}</td>
        <td className={signClass(h.pl_value)}>
          {h.pl_value >= 0 ? '+' : ''}
          {fmt(h.pl_value)}
        </td>
        <td className={signClass(h.pl_value)}>{fmtPct(h.pl_percent)}</td>
      </tr>
      {isExpanded && (
        <tr className="detail-row">
          <td colSpan={5}>
            <div className="detail-inner">
              <div className="detail-stats">
                <div className="detail-stat">
                  <span className="detail-stat__label">Name</span>
                  <span className="detail-stat__value">{h.name}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat__label">Ticker</span>
                  <span className="detail-stat__value">{h.ticker || '—'}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat__label">Qty</span>
                  <span className="detail-stat__value">{fmt(h.quantity, 4)}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat__label">Avg cost</span>
                  <span className="detail-stat__value">{fmt(h.avg_cost)}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat__label">Invested</span>
                  <span className="detail-stat__value">{fmt(h.invested)}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat__label">Price</span>
                  <span className="detail-stat__value">{fmt(h.current_price)}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat__label">Value</span>
                  <span className="detail-stat__value">{fmt(h.current_value)}</span>
                </div>
                <div className="detail-stat">
                  <span className="detail-stat__label">P/L</span>
                  <span className={`detail-stat__value ${signClass(h.pl_value)}`}>
                    {h.pl_value >= 0 ? '+' : ''}{fmt(h.pl_value)} ({fmtPct(h.pl_percent)})
                  </span>
                </div>
              </div>
              <div className="detail-actions">
                <button
                  className="btn btn--ghost"
                  title="Edit investment (name, ticker)"
                  onClick={() => setPanel({ mode: 'edit', investment: h })}
                >
                  <Icon path={mdiPencil} size={0.7} /> Edit
                </button>
                <button
                  className="btn btn--ghost"
                  title="Set current price"
                  onClick={() => setPanel({ mode: 'price', investment: h })}
                >
                  <Icon path={mdiCash} size={0.7} /> Set price
                </button>
                <button
                  className="btn btn--ghost"
                  title="Record transaction"
                  onClick={() => setPanel({ mode: 'tx', investment: h })}
                >
                  <Icon path={mdiPlus} size={0.7} /> Add transaction
                </button>
              </div>
              <h4>Transactions — {h.name}</h4>
              {(investmentTx || []).length === 0 ? (
                <p className="no-transactions">No transactions recorded</p>
              ) : (
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(investmentTx || []).map((t) => (
                      <tr key={t.id}>
                        <td>{t.transaction_date}</td>
                        <td>{t.type}</td>
                        <td className={itxSignClass(t)}>{itxDisplay(t)}</td>
                        <td>{t.quantity !== null ? fmt(t.quantity, 4) : '—'}</td>
                        <td>{t.price !== null ? fmt(t.price) : '—'}</td>
                        <td>
                          <button
                            className="btn btn--ghost"
                            title="Edit transaction"
                            onClick={() => setPanel({ mode: 'tx-edit', investment: h, tx: t })}
                          >
                            <Icon path={mdiPencil} size={0.7} />
                          </button>
                          <button
                            className="btn btn--ghost"
                            title="Duplicate transaction"
                            onClick={() => setPanel({ mode: 'tx-dup', investment: h, tx: t })}
                          >
                            <Icon path={mdiContentCopy} size={0.7} />
                          </button>
                          <button
                            className="btn btn--ghost"
                            title="Delete transaction"
                            onClick={() => {
                              if (window.confirm('Delete this transaction? The linked main transaction will also be removed.')) {
                                investmentsApi
                                  .removeTransaction(h.id, t.id)
                                  .then(refreshAll);
                              }
                            }}
                          >
                            <Icon path={mdiDelete} size={0.7} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

// ------------------------------------------------------------------
// Slide-in form
// ------------------------------------------------------------------

const InvestmentForm = ({ mode, investment, savings, account, presetType, tx, accounts, onCancel, onSuccess }) => {
  const isTxEdit = mode === 'tx-edit';
  const isTx = mode === 'tx' || mode === 'tx-edit' || mode === 'tx-dup';
  const [formData, setFormData] = useState(() => ({
    account_id: account?.account_id || accounts[0]?.id || accounts[0]?.account_id || '',
    investment_type: investment?.type || 'STOCK',
    name: investment?.name || '',
    ticker: investment?.ticker || '',
    currency: investment?.currency || accounts[0]?.currency || 'EUR',
    interest_rate: investment?.interest_rate ?? '',
    start_date: investment?.start_date || todayString(),
    // transaction fields (tx-edit/tx-dup prefill from the row being edited)
    type: (isTx ? tx?.type : null) || presetType || 'BUY',
    amount: tx?.amount ?? '',
    quantity: tx?.quantity ?? '',
    price: tx?.price ?? '',
    transaction_date: (isTx && tx?.transaction_date) || todayString(),
    notes: (isTx && tx?.notes) || '',
    // price fields
    price_value: investment?.current_price ?? '',
    as_of_date: todayString(),
    // apy fields
    apy_value: savings?.current_apy ?? investment?.current_apy ?? '',
    effective_date: todayString(),
  }));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, type: presetType || 'BUY' }));
  }, [presetType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Quantity / price / amount are linked (amount = quantity × price). The
  // derivation runs on blur: whatever two of the three fields hold at that
  // moment determines the third (the just-left field keeps priority).
  const deriveTxFields = (name) => {
    setFormData((prev) => {
      const num = (v) => {
        const n = parseFloat(v);
        return Number.isNaN(n) ? null : n;
      };
      const q = num(prev.quantity);
      const p = num(prev.price);
      const a = num(prev.amount);
      const next = {};
      if (name === 'quantity') {
        if (q !== null && p !== null && p > 0) {
          next.amount = String(+(q * p).toFixed(2));
        } else if (q !== null && q > 0 && a !== null) {
          next.price = String(+(a / q).toFixed(4));
        }
      } else if (name === 'price') {
        if (q !== null && q > 0 && p !== null && p > 0) {
          next.amount = String(+(q * p).toFixed(2));
        } else if (p !== null && p > 0 && a !== null && q === null) {
          next.quantity = String(+(a / p).toFixed(4));
        }
      } else if (name === 'amount') {
        if (q !== null && q > 0) {
          next.price = String(+(a / q).toFixed(4));
        } else if (p !== null && p > 0) {
          next.quantity = String(+(a / p).toFixed(4));
        }
      }
      return Object.keys(next).length ? { ...prev, ...next } : prev;
    });
  };

  const selectedType = formData.investment_type;
  const isSecurityType = SECURITY_TYPES.includes(selectedType);
  const isDepositType = DEPOSIT_TYPES.includes(selectedType);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (mode === 'new') {
        await investmentsApi.create({
          account_id: formData.account_id,
          investment_type: formData.investment_type,
          name: formData.name,
          ticker: isSecurityType ? formData.ticker.toUpperCase() : null,
          currency: formData.currency,
          interest_rate: isDepositType && formData.interest_rate !== '' ? formData.interest_rate : null,
          start_date: formData.start_date,
        });
      } else if (mode === 'edit') {
        await investmentsApi.update(investment.id, {
          name: formData.name,
          ticker: formData.ticker ? formData.ticker.toUpperCase() : null,
          currency: formData.currency,
          investment_type: formData.investment_type,
        });
      } else if (isTx) {
        const payload = {
          type: formData.type,
          amount: formData.amount,
          quantity: formData.quantity === '' ? null : formData.quantity,
          price: formData.price === '' ? null : formData.price,
          transaction_date: formData.transaction_date,
          notes: formData.notes,
        };
        if (isTxEdit) {
          await investmentsApi.updateTransaction(investment.id, tx.id, payload);
        } else {
          await investmentsApi.addTransaction(investment.id, payload);
        }
      } else if (mode === 'price') {
        if (!investment.ticker) {
          setFormError('Set a ticker first (pencil button) — the price book is keyed by ticker.');
          setSaving(false);
          return;
        }
        await investmentsApi.setPrice({
          ticker: investment.ticker,
          currency: investment.currency,
          price: formData.price_value,
          as_of_date: formData.as_of_date,
          source: 'MANUAL',
        });
      } else if (mode === 'apy') {
        if (savings) {
          await investmentsApi.addSavingsApy(savings.account_id, {
            apy: formData.apy_value,
            effective_date: formData.effective_date,
          });
        } else {
          await investmentsApi.addApy(investment.id, {
            apy: formData.apy_value,
            effective_date: formData.effective_date,
          });
        }
      }
      onSuccess();
    } catch (e2) {
      const msg = e2.response?.data?.error || 'Request failed';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="form" onSubmit={submit}>
      {formError && <div className="form-error">{formError}</div>}

      {mode === 'new' && (
        <>
          <label>
            Account
            <select
              name="account_id"
              value={formData.account_id}
              onChange={handleChange}
              required
            >
              {accounts.map((a) => (
                <option key={a.id ?? a.account_id} value={a.id ?? a.account_id}>
                  {a.name ?? a.account_name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Type
            <select
              name="investment_type"
              value={formData.investment_type}
              onChange={handleChange}
            >
              {INVESTMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replace('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <label>
            Name
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Nvidia, S&P 500 ETF, Revolut deposit"
              required
            />
          </label>
          {isSecurityType && (
            <label>
              Ticker
              <input
                name="ticker"
                value={formData.ticker}
                onChange={handleChange}
                placeholder="e.g. NVDA"
              />
            </label>
          )}
          <label>
            Currency
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          {isDepositType && (
            <label>
              APY (%)
              <input
                name="interest_rate"
                type="number"
                step="0.01"
                value={formData.interest_rate}
                onChange={handleChange}
                placeholder="e.g. 3.65"
              />
            </label>
          )}
          <label>
            Start date
            <input
              name="start_date"
              type="date"
              value={formData.start_date}
              onChange={handleChange}
              required
            />
          </label>
        </>
      )}

      {mode === 'edit' && (
        <>
          <p className="form-hint">
            The ticker links this holding to the shared price book — every account holding the
            same ticker shares one price. Currency is the product's own currency; totals are
            converted at the current rate.
          </p>
          <label>
            Name
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </label>
          <label>
            Ticker
            <input
              name="ticker"
              value={formData.ticker}
              onChange={handleChange}
              placeholder="e.g. NVDA"
            />
          </label>
          <label>
            Currency
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label>
            Type
            <select
              name="investment_type"
              value={formData.investment_type}
              onChange={handleChange}
            >
              {INVESTMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replace('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          {DEPOSIT_TYPES.includes(formData.investment_type) && (
            <p className="form-hint">
              Deposits show a balance and APY history instead of quantity/price. BUY
              transactions recorded while this was a security are treated as the initial
              deposit balance.
            </p>
          )}
        </>
      )}

      {isTx && (
        <>
          <label>
            Type
            <select name="type" value={formData.type} onChange={handleChange}>
              {ITX_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label>
            Amount
            <input
              name="amount"
              type="number"
              step="0.01"
              min="0"
              value={formData.amount}
              onChange={handleChange}
              onBlur={() => deriveTxFields('amount')}
              required
            />
          </label>
          {(formData.type === 'BUY' || formData.type === 'SELL') && (
            <>
              <label>
                Quantity
                <input
                  name="quantity"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.quantity}
                  onChange={handleChange}
                  onBlur={() => deriveTxFields('quantity')}
                />
              </label>
              <label>
                Price per unit
                <input
                  name="price"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.price}
                  onChange={handleChange}
                  onBlur={() => deriveTxFields('price')}
                />
              </label>
            </>
          )}
          {isTxEdit && (
            <p className="form-hint">
              The linked cash transaction (amount, type direction, date, notes) is updated
              together with this row.
            </p>
          )}
          <label>
            Date
            <input
              name="transaction_date"
              type="date"
              value={formData.transaction_date}
              onChange={handleChange}
              required
            />
          </label>
          <label>
            Notes
            <input name="notes" value={formData.notes} onChange={handleChange} />
          </label>
        </>
      )}

      {mode === 'price' && (
        <>
          {!investment.ticker ? (
            <p className="form-hint">
              This holding has no ticker yet. Set one first (pencil button) — the price book is
              keyed by ticker and refuses to store orphan prices.
            </p>
          ) : (
            <>
              <p className="form-hint">
                Price is shared across all accounts holding {investment.ticker} ({investment.currency}).
              </p>
              <label>
                Price
                <input
                  name="price_value"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.price_value}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </label>
              <label>
                As of date
                <input
                  name="as_of_date"
                  type="date"
                  value={formData.as_of_date}
                  onChange={handleChange}
                  required
                />
              </label>
            </>
          )}
        </>
      )}

      {mode === 'apy' && (
        <>
          <p className="form-hint">
            The APY is applied from the effective date onwards (entries can be backdated;
            re-entering the same effective date replaces that entry). The estimated return
            on the dashboard updates automatically.
          </p>
          <label>
            APY (%)
            <input
              name="apy_value"
              type="number"
              step="0.01"
              value={formData.apy_value}
              onChange={handleChange}
              required
            />
          </label>
          <label>
            Effective date
            <input
              name="effective_date"
              type="date"
              value={formData.effective_date}
              onChange={handleChange}
              required
            />
          </label>
        </>
      )}

      <div className="form-actions">
        <button type="button" className="btn btn--secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export default InvestmentsPage;